/**
 * Catalog category detail/edit (ADMIN-PR2-a) — GET/PUT/DELETE /v1/categories/{key}.
 * Edit display_name / override_levels / property_schema, retire⇄restore (PUT
 * retired_at), and delete. Entry management is PR2-b (gating pending Ramon's A/B).
 */
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  deleteCategory,
  getCategory,
  updateCategory,
  type CategoryOut,
} from "../auth/mastersApi";
import { errorMessage, useAsync } from "../lib/useAsync";
import { GROUP_ORDER } from "../lib/catalogGroups";
import { ErrorBanner, PageHeader, Spinner, StatusBadge } from "../components/ui";
import { JsonField, parseJsonObject } from "../components/JsonField";

function fmtDate(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function CategoryDetailPage(): ReactNode {
  const { key = "" } = useParams();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useAsync<CategoryOut>(() => getCategory(key), [key]);

  const [displayName, setDisplayName] = useState("");
  const [categoryGroup, setCategoryGroup] = useState("");
  const [overrideLevels, setOverrideLevels] = useState("");
  const [schemaRaw, setSchemaRaw] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  // Hydrate the edit form when the category loads.
  useEffect(() => {
    if (data) {
      setDisplayName(data.display_name);
      setCategoryGroup(data.category_group ?? "");
      setOverrideLevels(data.override_levels.join(", "));
      setSchemaRaw(data.property_schema ? JSON.stringify(data.property_schema, null, 2) : "");
    }
  }, [data]);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setMutationError(null);
    const schema = parseJsonObject(schemaRaw);
    if (!schema.ok) {
      setMutationError("property_schema is not valid JSON.");
      return;
    }
    setBusy("save");
    try {
      await updateCategory(key, {
        display_name: displayName.trim(),
        // Empty input clears the family (→ "Other"); send null, not "".
        category_group: categoryGroup.trim() || null,
        override_levels: overrideLevels
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        property_schema: schema.value,
      });
      reload();
    } catch (err) {
      setMutationError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onToggleRetire(retired: boolean) {
    if (!window.confirm(retired ? "Restore this category?" : "Retire this category?")) return;
    setMutationError(null);
    setBusy("retire");
    try {
      await updateCategory(key, { retired_at: retired ? null : new Date().toISOString() });
      reload();
    } catch (err) {
      setMutationError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  async function onDelete() {
    if (!window.confirm(`Delete category "${key}"? This cannot be undone.`)) return;
    setMutationError(null);
    setBusy("delete");
    try {
      await deleteCategory(key);
      navigate("/catalog", { replace: true });
    } catch (err) {
      setMutationError(errorMessage(err));
      setBusy(null);
    }
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={errorMessage(error)} />;
  if (!data) return <ErrorBanner message="Category not found." />;

  const retired = data.retired_at !== null;

  return (
    <div className="space-y-5">
      <div>
        <Link to="/catalog" className="text-sm text-oxbo hover:underline">
          ← Catalog categories
        </Link>
      </div>
      <PageHeader title={data.display_name}>
        <StatusBadge status={retired ? "retired" : "active"} />
      </PageHeader>

      {mutationError && <ErrorBanner message={mutationError} />}

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <dl className="mb-4 grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Key</dt>
            <dd className="font-mono text-xs text-slate-700">{data.category_key}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Modified</dt>
            <dd className="text-slate-700">
              {fmtDate(data.modified_at)}
              {data.modified_by ? ` · ${data.modified_by}` : ""}
            </dd>
          </div>
        </dl>

        <form onSubmit={onSave} className="space-y-3">
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">Display name</span>
            <input
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1.5"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">Family (group)</span>
            <input
              list="catalog-group-options"
              value={categoryGroup}
              onChange={(e) => setCategoryGroup(e.target.value)}
              placeholder="Other (uncategorized)"
              className="w-full rounded border border-slate-300 px-2 py-1.5"
            />
            <datalist id="catalog-group-options">
              {GROUP_ORDER.map((g) => (
                <option key={g} value={g} />
              ))}
            </datalist>
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-slate-600">Override levels (comma-separated)</span>
            <input
              value={overrideLevels}
              onChange={(e) => setOverrideLevels(e.target.value)}
              className="w-full rounded border border-slate-300 px-2 py-1.5"
            />
          </label>
          <JsonField label="property_schema (JSON)" value={schemaRaw} onChange={setSchemaRaw} rows={8} />
          <button
            type="submit"
            disabled={busy === "save"}
            className="rounded bg-oxbo px-4 py-1.5 text-sm font-medium text-white hover:bg-oxbo-dark disabled:opacity-50"
          >
            {busy === "save" ? "Saving…" : "Save changes"}
          </button>
        </form>
      </section>

      <section className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-5">
        <button
          type="button"
          disabled={busy === "retire"}
          onClick={() => void onToggleRetire(retired)}
          className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
        >
          {retired ? "Restore" : "Retire"}
        </button>
        <button
          type="button"
          disabled={busy === "delete"}
          onClick={() => void onDelete()}
          className="rounded border border-red-300 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
        >
          Delete
        </button>
      </section>

      <section className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-5">
        <div className="text-sm text-slate-600">
          <span className="font-medium text-slate-700">Entries</span> — stock curation &amp;
          on-behalf-of org entries for this category.
        </div>
        <div className="flex gap-2">
          <Link
            to={`/catalog/${encodeURIComponent(key)}/resolved`}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50"
          >
            View resolved
          </Link>
          <Link
            to={`/catalog/${encodeURIComponent(key)}/entries`}
            className="rounded bg-oxbo px-3 py-1.5 text-sm font-medium text-white hover:bg-oxbo-dark"
          >
            Manage entries →
          </Link>
        </div>
      </section>
    </div>
  );
}
